import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, CircularProgress, Paper } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { WorkshopNLogo } from '../modules/workshop/pages/WorkshopNAdmin';

const accent = '#E05500';

export default function WorkshopJoin() {
  const { workshopId } = useParams();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [namn, setNamn] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      if (!auth.currentUser) await signInAnonymously(auth);
      const snap = await getDoc(doc(db, 'workshops', workshopId));
      if (snap.exists()) setWorkshop({ id: snap.id, ...snap.data() });
      setLoading(false);
    })();
  }, [workshopId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!namn.trim() || !email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      if (!auth.currentUser) await signInAnonymously(auth);
      await addDoc(collection(db, 'workshops', workshopId, 'registrations'), {
        namn: namn.trim(),
        email: email.trim().toLowerCase(),
        registeredAt: serverTimestamp(),
      });
      setDone(true);
    } catch {
      setError('Något gick fel. Försök igen.');
    }
    setSubmitting(false);
  }

  if (loading) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0a0a0a' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  if (!workshop) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0a0a0a' }}>
      <Typography color="error">Workshopen hittades inte.</Typography>
    </Box>
  );

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#0a0a0a',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3,
      position: 'relative', overflow: 'hidden',
    }}>
      {workshop.imageUrl && (
        <Box component="img" src={workshop.imageUrl} alt=""
          sx={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.12, zIndex: 0, pointerEvents: 'none' }} />
      )}
      <Paper elevation={0} sx={{
        position: 'relative', zIndex: 1, maxWidth: 420, width: '100%',
        p: { xs: 3, sm: 4 }, borderRadius: 3,
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      }}>
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <WorkshopNLogo />
          <Typography fontWeight={700} fontSize="1.15rem" textAlign="center" color="text.primary" mt={1.5} sx={{ lineHeight: 1.3 }}>
            {workshop.title}
          </Typography>
          {workshop.date && (
            <Typography color="text.secondary" fontSize="0.85rem">
              {new Date(workshop.date).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          )}
        </Box>

        {done ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: '#4ade80' }} />
            <Typography fontWeight={700} fontSize="1.1rem" textAlign="center">Du är nu anmäld!</Typography>
            <Typography color="text.secondary" fontSize="0.9rem" textAlign="center" sx={{ lineHeight: 1.6 }}>
              Vi hör av oss inför workshopen.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography color="text.secondary" fontSize="0.88rem" textAlign="center">
              Anmäl dig genom att fylla i ditt namn och din e-postadress.
            </Typography>
            <TextField
              label="Namn"
              value={namn}
              onChange={e => setNamn(e.target.value)}
              required fullWidth size="small"
              inputProps={{ style: { fontSize: 16 } }}
              autoComplete="name"
            />
            <TextField
              label="E-postadress"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required fullWidth size="small"
              inputProps={{ style: { fontSize: 16 } }}
              autoComplete="email"
            />
            {error && <Typography color="error" fontSize="0.82rem">{error}</Typography>}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || !namn.trim() || !email.trim()}
              sx={{
                bgcolor: accent, '&:hover': { bgcolor: '#C94D00' },
                textTransform: 'none', fontWeight: 700, py: 1.3, mt: 0.5,
                fontSize: '1rem',
              }}
            >
              {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Anmäl mig'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
